import { Component } from '@theme/component';
import {
  ThemeEvents,
  QuantitySelectorUpdateEvent,
} from '@theme/events';
import { morph } from '@theme/morph';
import {
  StandardEvents,
  ProductSelectEvent,
  CartLinesUpdateEvent,
  CartErrorEvent,
} from '@shopify/events';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 */

/**
 * Controls the sticky add-to-cart bar.
 *
 * The bar is shown only after the main product buy buttons have been
 * scrolled above the viewport. It is not tied to the footer, product
 * description markup, review apps or other product-specific page content.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber',
  ];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | undefined} */
  #setupRetryTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {number} */
  #setupAttempts = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');

    section?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    this.#getInitialQuantity();
    this.#setupIntersectionObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#buyButtonsIntersectionObserver = null;

    this.#abortController.abort();

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    if (this.#setupRetryTimeout) {
      clearTimeout(this.#setupRetryTimeout);
    }
  }

  /**
   * Finds the main product form and observes its buy-button area.
   * A short retry is used because product/app blocks can finish rendering
   * after the sticky custom element connects.
   */
  #setupIntersectionObserver() {
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#buyButtonsIntersectionObserver = null;

    const productForm = this.#getProductForm();

    if (!productForm) {
      this.#retryObserverSetup();
      return;
    }

    const buyButtonsBlock =
      productForm.closest('.buy-buttons-block') ?? productForm;

    this.#targetAddToCartButton = productForm.querySelector(
      '[ref="addToCartButton"]'
    );

    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry) return;

        const rect = entry.target.getBoundingClientRect();

        const buyButtonsAreAboveViewport =
          !entry.isIntersecting && rect.bottom <= 0;

        if (buyButtonsAreAboveViewport) {
          this.#showStickyBar();
        } else {
          this.#hideStickyBar();
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);

    const initialRect = buyButtonsBlock.getBoundingClientRect();

    if (initialRect.bottom <= 0) {
      this.#showStickyBar();
    } else {
      this.#hideStickyBar();
    }
  }

  /**
   * Retries setup briefly when an app or dynamic section delays the form.
   */
  #retryObserverSetup() {
    if (
      this.#abortController.signal.aborted ||
      this.#setupAttempts >= 20
    ) {
      return;
    }

    this.#setupAttempts += 1;

    if (this.#setupRetryTimeout) {
      clearTimeout(this.#setupRetryTimeout);
    }

    this.#setupRetryTimeout = setTimeout(() => {
      this.#setupIntersectionObserver();
    }, 100);
  }

  /**
   * Handles the sticky Add to Cart button click.
   */
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton) {
      const productForm = this.#getProductForm();

      this.#targetAddToCartButton =
        productForm?.querySelector('[ref="addToCartButton"]') ?? null;
    }

    if (
      !this.#targetAddToCartButton ||
      this.#targetAddToCartButton.disabled
    ) {
      return;
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const stickyButton = this.refs.addToCartButton;

    if (stickyButton.dataset.added !== 'true') {
      stickyButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    this.#resetTimeout = setTimeout(() => {
      stickyButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Updates the sticky bar after a variant selection.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    const { optionValueId } = event.detail ?? {};

    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const {
          html,
          productId,
          resource: variant,
        } = detail;

        if (
          productId &&
          String(productId) !== String(this.dataset.productId)
        ) {
          return;
        }

        const newStickyAddToCart =
          /** @type {HTMLElement | null} */ (
            html.querySelector('sticky-add-to-cart')
          );

        if (!newStickyAddToCart) return;

        const newStickyBar =
          newStickyAddToCart.querySelector(
            '[ref="stickyBar"]'
          );

        if (!newStickyBar) return;

        const currentStuck =
          this.refs.stickyBar.getAttribute('data-stuck') ||
          'false';

        const variantAvailable =
          newStickyAddToCart.dataset.variantAvailable ??
          'false';

        morph(
          this.refs.stickyBar,
          newStickyBar,
          { childrenOnly: true }
        );

        this.refs.stickyBar.setAttribute(
          'data-stuck',
          currentStuck
        );

        this.dataset.variantAvailable =
          variantAvailable;

        if (variant?.id) {
          this.dataset.currentVariantId =
            String(variant.id);
        }

        const productForm = this.#getProductForm();

        this.#targetAddToCartButton =
          productForm?.querySelector(
            '[ref="addToCartButton"]'
          ) ?? null;

        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[sticky-add-to-cart] Variant update failed:',
            error
          );
        }
      });
  };

  /**
   * Updates the displayed option title when no matching variant exists.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement = this.querySelector(
      '.sticky-add-to-cart__variant'
    );

    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section');

    const variantPicker =
      section?.querySelector(
        `variant-picker[data-product-id="${productId}"]`
      ) ??
      document.querySelector(
        `variant-picker[data-product-id="${productId}"]`
      );

    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll('input:checked')
    )
      .map(
        (option) =>
          /** @type {HTMLInputElement} */ (option).value
      )
      .filter(Boolean)
      .join(' / ');

    if (selectedOptions) {
      variantTitleElement.textContent =
        selectedOptions;
    }
  };

  /**
   * Resets the puppet flag after a cart request succeeds or fails.
   *
   * @param {CartLinesUpdateEvent | CartErrorEvent} event
   */
  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet =
          'false';
      }
    };

    if (
      'promise' in event &&
      event.promise instanceof Promise
    ) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Updates the quantity shown in the sticky button.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    const eventTarget = event.target;
    const section = this.closest('.shopify-section');

    if (
      eventTarget instanceof Element &&
      section &&
      !section.contains(eventTarget)
    ) {
      return;
    }

    this.#currentQuantity =
      event.detail.quantity;

    this.#updateButtonText();
  };

  #showStickyBar() {
    if (this.#isStuck) return;

    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    if (
      !this.#isStuck &&
      this.refs.stickyBar.dataset.stuck === 'false'
    ) {
      return;
    }

    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  /**
   * Gets the main product form belonging to this section.
   *
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    const sectionElement =
      this.closest('.shopify-section');

    if (!productId || !sectionElement) {
      return null;
    }

    return (
      sectionElement.querySelector(
        `product-form-component[data-product-id="${productId}"]`
      ) ??
      sectionElement.querySelector(
        'product-form-component'
      )
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity =
      Number.parseInt(
        this.dataset.initialQuantity || '1',
        10
      ) || 1;

    this.#updateButtonText();
  }

  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber,
    } = this.refs;

    const available =
      this.dataset.variantAvailable === 'true' &&
      !addToCartButton.disabled;

    quantityNumber.textContent =
      String(this.#currentQuantity);

    if (
      available &&
      this.#currentQuantity > 1
    ) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}